import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Fallback } from './Fallback';
import { PlanRow, TotalPlanRow } from './PlanRow';
import {
  calculateTotalPrice,
  getButtonText,
  getDescriptionText,
  getHeaderText,
  getMembersAfterUpdate,
} from './helpers';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';

import { batchDeleteMembers, batchUpdateMembers, updateSubscription } from '~/lib/.client/api/organizations';
import {
  trackTeamMemberRoleChanged,
  trackTeamMemberUpgraded,
  trackTeamMemberDowngraded,
  trackTeamMemberRemoved,
} from '~/lib/analytics/team-membership';
import { sendAnalyticsEvent } from '~/lib/analytics.client';

import { useClearUpdateTeamData } from '~/lib/hooks/useClearUpdateTeamData';
import { organizationMembersStore } from '~/lib/stores/organizationMembers';

import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { formatMemberEmails } from '~/utils/format';
import { teamsPricingList } from '~/utils/pricing';
import { compileTimeUnreachable } from '~/utils/unreachable';
import { withSpinner } from '~/utils/withSpinner';

interface UpdateTeamScreenProps {
  showPricingOverlay: VoidFunction;
}

export const UpdateTeamScreen = ({ showPricingOverlay }: UpdateTeamScreenProps) => {
  const updateData = useStore(teamStore.updateTeamData);
  const clearUpdateTeamData = useClearUpdateTeamData();

  const orgMembers = useStore(organizationMembersStore.activeOrgMembers);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (updateData?.type) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamMembersChangeConfirmationModalOpened,
          properties: {
            organization_id: updateData.organization.id,
            action: updateData.type,
          },
        },
      });
    }
  }, [updateData]);

  if (!updateData) {
    return null;
  }

  const subscription = updateData.organization.billing?.subscription;
  const currentTier = updateData.organization.billing?.tier;
  const currentPlan = teamsPricingList.find((plan) => plan.level === `teams_${currentTier}`);
  const newPlan = teamsPricingList.find((plan) => plan.level === updateData.newPlan) ?? currentPlan;

  if (!subscription || !currentPlan || !newPlan) {
    return <Fallback showPricingOverlay={showPricingOverlay} />;
  }

  const isYearly = subscription.interval === 'yearly';
  const newMembers = getMembersAfterUpdate(updateData, orgMembers);

  const totalPrice = ['upgrade', 'downgrade', 'remove'].includes(updateData.type)
    ? calculateTotalPrice({
        newMembersCount: newMembers.length,
        oldMembersCount: orgMembers.filter((m) => m.withMembership).length,
        planPrice: isYearly ? newPlan.rateMonthly.year : newPlan.rateMonthly.month,
        isYearly,
      })
    : null;

  const handleConfirm = async () => {
    await withSpinner({
      action: async () => {
        try {
          switch (updateData.type) {
            case 'upgrade':
            case 'downgrade': {
              await updateSubscription({
                organization: updateData.organization,
                interval: subscription.interval,
                level: currentPlan.level,
                invitees: newMembers.filter((member) => !member.downgraded).map((m) => m.user.providerId),
              });
              toast.success(updateData.type === 'upgrade' ? 'Upgraded successfully' : 'Downgraded successfully');

              // track member subscription changes
              const currentUser = userStore.getUser();

              if (updateData.type === 'upgrade') {
                trackTeamMemberUpgraded(updateData.organization, updateData.members, currentUser, orgMembers);
              } else {
                trackTeamMemberDowngraded(updateData.organization, updateData.members, currentUser, orgMembers);
              }

              break;
            }

            case 'remove': {
              await batchDeleteMembers({
                organization: updateData.organization,
                members: updateData.members,
              });
              toast.success(
                updateData.members.length > 1 ? 'The members have been deleted' : 'The member has been deleted',
              );

              // track member removal
              const currentUser = userStore.getUser();
              trackTeamMemberRemoved(updateData.organization, updateData.members, currentUser, orgMembers);
              break;
            }

            case 'change_role': {
              const members = updateData.members.map((member) => ({
                ...member,
                role: updateData.newRole ?? member.role,
              }));
              await batchUpdateMembers({
                organization: updateData.organization,
                members,
              });
              toast.success(members.length > 1 ? 'The members have been updated' : 'The member has been updated');

              // track role changes
              if (updateData.newRole) {
                const currentUser = userStore.getUser();

                const roleChanges = updateData.members.map((member) => ({
                  member,
                  oldRole: member.role,
                  newRole: updateData.newRole!,
                }));
                trackTeamMemberRoleChanged(updateData.organization, roleChanges, currentUser, orgMembers);
              }

              break;
            }

            default: {
              compileTimeUnreachable(updateData.type);
            }
          }
          closeUpdateScreen();
          teamStore.selectedMembers.set([]);
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      },
      onStateUpdate: setIsUpdating,
    });
  };

  const closeUpdateScreen = () => {
    clearUpdateTeamData();
  };

  return (
    <div className="flex flex-col gap-6 items-start min-w-0 pt-6 pl-6 pr-12 bg-bolt-ds-surfaceAltDark">
      <div className="flex items-start gap-3 max-w-full">
        <IconButton icon="i-heroicons:arrow-left-solid" size="md" variant="ghost" onClick={closeUpdateScreen} />
        <div className="flex flex-col gap-1 pt-1">
          <div className="text-lg font-semibold text-bolt-ds-textPrimary truncate">
            {getHeaderText(updateData.type)}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 w-full">
        <div className="flex justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-sm font-semibold text-bolt-ds-textPrimary">
              {getHeaderText(updateData.type).replace('confirmation', '')}
            </div>
            <div className="text-sm text-bolt-ds-textTertiary">
              {getDescriptionText(updateData.type, updateData.newRole)}
            </div>
          </div>
          <div className="text-sm max-w-[50%] text-right text-bolt-ds-textTertiary">
            {formatMemberEmails(updateData.members.map((m) => m.user.email))}
          </div>
        </div>

        <PlanRow
          title={`Current Teams ${currentPlan.namePrice ?? ''} Plan`}
          rateMonthly={isYearly ? currentPlan.rateMonthly.year : currentPlan.rateMonthly.month}
          seats={orgMembers.filter((m) => m.withMembership).length}
          subscriptionInterval={subscription.interval}
        />
        <PlanRow
          title={`New Teams ${newPlan.namePrice ?? ''} Plan`}
          rateMonthly={isYearly ? newPlan.rateMonthly.year : newPlan.rateMonthly.month}
          seats={
            updateData.type === 'change_role' ? orgMembers.filter((m) => m.withMembership).length : newMembers.length
          }
          subscriptionInterval={subscription.interval}
        />
        <TotalPlanRow totalPrice={totalPrice} updateType={updateData.type} />
      </div>

      <div className="py-6 w-full">
        <div className="h-px w-full bg-bolt-ds-borderDivider mb-6" />
        <div className="flex justify-end gap-4">
          <Button
            variant={updateData.type === 'downgrade' ? 'secondary' : 'primary'}
            onClick={handleConfirm}
            className="gap-2 items-center px-4"
            endIcon={updateData.type === 'upgrade' ? 'i-heroicons:arrow-top-right-on-square' : undefined}
            disabled={isUpdating || newMembers.length === 0}
            loading={isUpdating}
          >
            <span className="text-base">{getButtonText(updateData.type, subscription)}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
